// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';

import type { CertUpload, Dispatch } from 'state/types';
import DialogPanel from 'view/App/DialogPanels/DialogPanel';
import { hideDialogPanel } from 'state/commands/dialogPanelCommands';
import localizedStrings from 'util/localizedStrings';
import { CERT_UPLOAD_PANEL_ID } from 'util/constants';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import { getActivePanelId } from 'state/selectors';
import { uploadCert } from 'state/commands/certCommands';

type Props = {
  activePanelId: ?string,
  hideDialogPanel: typeof hideDialogPanel,
  intl: IntlShape,
  uploadCert: typeof uploadCert,
};

type State = {
  mode: 'none' | 'unbundled' | 'pkcs',
  mainCertFile?: any,
  intermediateCertFile1?: any,
  intermediateCertFile2?: any,
  rootCertFile?: any,
  password?: string,
};

const MODE_NONE = 'none';
const MODE_UNBUNDLED = 'unbundled';
const MODE_PKCS = 'pkcs';
const allCertFileTypes = '.cer, .crt, .p7, .p7b, .p12, .pem, .pfx';
const pemFileTypes = '.cer, .crt, .pem';

class CertUploadDialog extends Component<Props, State> {
  isOkEnabled: Function;
  onFileSelected: Function;
  onPasswordUpdated: Function;
  renderFilePicker: Function;
  submit: Function;

  constructor() {
    super();
    this.isOkEnabled = this.isOkEnabled.bind(this);
    this.onFileSelected = this.onFileSelected.bind(this);
    this.onPasswordUpdated = this.onPasswordUpdated.bind(this);
    this.renderFilePicker = this.renderFilePicker.bind(this);
    this.submit = this.submit.bind(this);
  }

  state = {
    mode: MODE_NONE,
    mainCertFile: undefined,
    intermediateCertFile1: undefined,
    intermediateCertFile2: undefined,
    rootCertFile: undefined,
    password: undefined,
  };

  onFileSelected(e: any) {
    const file = e.target.files && e.target.files[0];
    if (e.target.name === 'mainCertFile') {
      // TODO: if mode is unbundled, require there be a pending request?
      this.setState({
        mainCertFile: file,
        mode: this.modeForMainFile(file),
      });
    } else {
      this.setState({ [e.target.name]: file });
    }
  }

  onPasswordUpdated(e: any) {
    this.setState({ password: e.target.value });
  }

  modeForMainFile(file) {
    if (!file) {
      return MODE_NONE;
    }
    const extension = file.name.split('.').pop();
    return this.isPemExtension(extension) ? MODE_UNBUNDLED : MODE_PKCS;
  }

  isPemExtension(extension) {
    return extension === 'pem' || extension === 'cer' || extension === 'crt';
  }

  isOkEnabled() {
    const { mode, rootCertFile } = this.state;
    if (mode === MODE_NONE) {
      return false;
    } else if (mode === MODE_PKCS) {
      return true;
    } else {
      return !!rootCertFile;
    }
  }

  submit() {
    const {
      mainCertFile,
      intermediateCertFile1,
      intermediateCertFile2,
      rootCertFile,
      password,
    } = this.state;
    const uploadData: CertUpload = {
      mainCertFile,
      intermediateCertFile1,
      intermediateCertFile2,
      rootCertFile,
      password,
    };
    this.props.uploadCert(uploadData);
  }

  renderFilePicker(name: string, labelId: any, accept: string, file?: any) {
    const className = `${
      file ? '' : 'is-primary'
    } file has-name is-fullwidth is-right`;
    return (
      <div className="field">
        <label className="label is-medium">
          <FormattedMessage {...labelId} />
        </label>
        <div className={className}>
          <label className="file-label">
            <input
              accept={accept}
              className="file-input"
              name={name}
              onChange={this.onFileSelected}
              type="file"
              data-test-name="cert-input"
            />
            <span className="file-cta">
              <span className="file-label">
                <FormattedMessage {...localizedStrings.CERT_FILE_PROMPT} />
              </span>
            </span>
            <span className="file-name is-right">
              {(file && file.name) || (
                <FormattedMessage {...localizedStrings.NO_FILE_SELECTED} />
              )}
            </span>
          </label>
        </div>
        {!file && <span className="help is-indented">({accept})</span>}
        <div className="vertical-spacer-large" />
      </div>
    );
  }

  render() {
    const {
      activePanelId,
      intl: { formatMessage },
    } = this.props;
    const isUnbundledCert = this.state.mode === MODE_UNBUNDLED;
    const isPkcsCert = this.state.mode === MODE_PKCS;

    return (
      <DialogPanel
        activePanelId={activePanelId}
        contentStyle={{ width: '500px', height: null }}
        customButtonText={formatMessage(localizedStrings.USER_IMPORT_IMPORT)}
        hideDialogPanel={this.props.hideDialogPanel}
        isOkEnabled={this.isOkEnabled}
        onSave={this.submit}
        panelId={CERT_UPLOAD_PANEL_ID}
        panelTitle={formatMessage(localizedStrings.CERT_IMPORT)}
        showCancel
      >
        <form className="portfolio-form-container">
          {this.renderFilePicker(
            'mainCertFile',
            localizedStrings.CERT_FILE,
            allCertFileTypes,
            this.state.mainCertFile
          )}

          {isUnbundledCert && (
            <div>
              {this.renderFilePicker(
                'intermediateCertFile1',
                localizedStrings.CERT_FILE_INT_1,
                pemFileTypes,
                this.state.intermediateCertFile1
              )}
              {this.renderFilePicker(
                'intermediateCertFile2',
                localizedStrings.CERT_FILE_INT_2,
                pemFileTypes,
                this.state.intermediateCertFile2
              )}
              {this.renderFilePicker(
                'rootCertFile',
                localizedStrings.CERT_FILE_ROOT,
                pemFileTypes,
                this.state.rootCertFile
              )}
            </div>
          )}

          {isPkcsCert && (
            <div>
              <div className="field">
                <label className="label is-medium">
                  <FormattedMessage {...localizedStrings.CERT_FILE_PASSWORD} />
                </label>
                <div className="control">
                  <input
                    className="input"
                    type="password"
                    onChange={this.onPasswordUpdated}
                  />
                  <span className="help">
                    <FormattedMessage
                      {...localizedStrings.CERT_FILE_PASSWORD_HELP}
                    />
                  </span>
                </div>
              </div>
            </div>
          )}
        </form>
      </DialogPanel>
    );
  }
}

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({ hideDialogPanel, uploadCert })
)(injectIntl(CertUploadDialog));



// WEBPACK FOOTER //
// ./src/view/Pages/CertsPage/CertUploadDialog.js